/**
 * Material Design Tokens for Hivetivity Dashboard
 * Integrates Hivetivity brand colors with Material Design principles
 */

import { colors as hivetivityColors } from './design-tokens';

// Material Design 3 Color System with Hivetivity Branding
export const materialColors = {
  // Primary (Hivetivity Blue)
  primary: {
    50: hivetivityColors.primary[50],   // #eff6ff
    100: hivetivityColors.primary[100], // #dbeafe
    500: hivetivityColors.primary[500], // #3b82f6
    600: hivetivityColors.primary[600], // #2563eb
    700: hivetivityColors.primary[700], // #1d4ed8
    900: hivetivityColors.primary[900], // #1e3a8a
  },

  // Secondary (Material Purple for accents)
  secondary: {
    50: '#f3e5f5',
    100: '#e1bee7',
    500: '#9c27b0',
    600: '#8e24aa',
    700: '#7b1fa2',
    900: '#4a148c',
  },

  // Success (Hivetivity Green)
  success: {
    50: hivetivityColors.success[50],   // #f0fdf4
    100: hivetivityColors.success[100], // #dcfce7
    500: hivetivityColors.success[500], // #22c55e
    600: hivetivityColors.success[600], // #16a34a
    700: hivetivityColors.success[700], // #15803d
    800: hivetivityColors.success[800], // #166534
  },

  // Warning (Hivetivity Orange)
  warning: {
    50: hivetivityColors.warning[50],   // #fffbeb
    100: hivetivityColors.warning[100], // #fef3c7
    500: hivetivityColors.warning[500], // #f59e0b
    600: hivetivityColors.warning[600], // #d97706
    700: hivetivityColors.warning[700], // #b45309
    800: hivetivityColors.warning[800], // #92400e
  },

  // Error (Hivetivity Red)
  error: {
    50: hivetivityColors.error[50],   // #fef2f2
    100: hivetivityColors.error[100], // #fee2e2
    500: hivetivityColors.error[500], // #ef4444
    600: hivetivityColors.error[600], // #dc2626
    700: hivetivityColors.error[700], // #b91c1c
    800: hivetivityColors.error[800], // #991b1b
  },

  // Neutral/Surface colors
  neutral: hivetivityColors.neutral,

  // Surface colors for Material Design
  surface: {
    background: '#f5f5f5',     // Page background
    surface: '#ffffff',        // Card surface
    surfaceVariant: '#f9fafb', // Alternate surface
    outline: '#e5e7eb',        // Borders
    outlineVariant: '#f3f4f6', // Subtle borders
  },
} as const;

// Material Design Spacing System (8px base unit)
export const spacing = {
  0: '0px',
  1: '4px',   // 0.5 * 8px
  2: '8px',   // 1 * 8px
  3: '12px',  // 1.5 * 8px
  4: '16px',  // 2 * 8px
  5: '20px',  // 2.5 * 8px
  6: '24px',  // 3 * 8px
  8: '32px',  // 4 * 8px
  10: '40px', // 5 * 8px
  12: '48px', // 6 * 8px
  16: '64px', // 8 * 8px
  20: '80px', // 10 * 8px
  24: '96px', // 12 * 8px
} as const;

// Material Design Elevation (Shadow) System
export const elevation = {
  0: 'none',
  1: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
  2: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
  3: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
  4: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
  5: '0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22)',
} as const;

// Material Design Border Radius
export const borderRadius = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  '3xl': '24px',
  full: '9999px',
} as const;

// Material Design Typography Scale
export const typography = {
  displayLarge: {
    fontSize: '57px',
    lineHeight: '64px',
    fontWeight: '400',
    letterSpacing: '-0.25px',
  },
  displayMedium: {
    fontSize: '45px',
    lineHeight: '52px',
    fontWeight: '400',
    letterSpacing: '0px',
  },
  displaySmall: {
    fontSize: '36px',
    lineHeight: '44px',
    fontWeight: '400',
    letterSpacing: '0px',
  },
  headlineLarge: {
    fontSize: '32px',
    lineHeight: '40px',
    fontWeight: '600',
    letterSpacing: '0px',
  },
  headlineMedium: {
    fontSize: '28px',
    lineHeight: '36px',
    fontWeight: '600',
    letterSpacing: '0px',
  },
  headlineSmall: {
    fontSize: '24px',
    lineHeight: '32px',
    fontWeight: '600',
    letterSpacing: '0px',
  },
  titleLarge: {
    fontSize: '22px',
    lineHeight: '28px',
    fontWeight: '500',
    letterSpacing: '0px',
  },
  titleMedium: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: '500',
    letterSpacing: '0.15px',
  },
  titleSmall: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: '500',
    letterSpacing: '0.1px',
  },
  bodyLarge: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: '400',
    letterSpacing: '0.15px',
  },
  bodyMedium: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: '400',
    letterSpacing: '0.25px',
  },
  bodySmall: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '400',
    letterSpacing: '0.4px',
  },
  labelLarge: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: '500',
    letterSpacing: '0.1px',
  },
  labelMedium: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '500',
    letterSpacing: '0.5px',
  },
  labelSmall: {
    fontSize: '11px',
    lineHeight: '16px',
    fontWeight: '500',
    letterSpacing: '0.5px',
  },
} as const;

// Dashboard-specific color themes for metric cards
export const metricCardThemes = {
  primary: {
    icon: materialColors.primary[600],
    background: materialColors.primary[50],
    text: materialColors.primary[900],
    accent: materialColors.primary[100],
  },
  success: {
    icon: materialColors.success[600],
    background: materialColors.success[50],
    text: materialColors.success[800],
    accent: materialColors.success[100],
  },
  warning: {
    icon: materialColors.warning[600],
    background: materialColors.warning[50],
    text: materialColors.warning[800],
    accent: materialColors.warning[100],
  },
  secondary: {
    icon: materialColors.secondary[600],
    background: materialColors.secondary[50],
    text: materialColors.secondary[900],
    accent: materialColors.secondary[100],
  },
} as const;

// Animation curves (Material Design Motion)
export const motionCurves = {
  standard: 'cubic-bezier(0.2, 0.0, 0, 1.0)',
  decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
  accelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
  sharp: 'cubic-bezier(0.4, 0.0, 0.6, 1)',
} as const;

// Animation durations
export const motionDurations = {
  short1: '75ms',
  short2: '150ms',
  short3: '200ms',
  short4: '250ms',
  medium1: '300ms',
  medium2: '350ms',
  medium3: '400ms',
  medium4: '450ms',
  long1: '500ms',
  long2: '550ms',
  long3: '600ms',
  long4: '650ms',
} as const;

// Dashboard grid specifications
export const dashboardGrid = {
  container: {
    maxWidth: '1200px',
    padding: spacing[6],  // 24px
    gap: spacing[6],      // 24px
  },
  metricCard: {
    minHeight: '140px',
    padding: spacing[6],  // 24px
    borderRadius: borderRadius.xl, // 16px
  },
  chartCard: {
    minHeight: '300px',
    padding: spacing[6],  // 24px
    borderRadius: borderRadius.xl, // 16px
  },
  dataCard: {
    padding: spacing[6],  // 24px
    borderRadius: borderRadius.xl, // 16px
  },
} as const;

// Export utility functions
export const getElevationStyle = (level: keyof typeof elevation) => ({
  boxShadow: elevation[level],
});

export const getSpacing = (...values: (keyof typeof spacing)[]) => 
  values.map(value => spacing[value]).join(' ');

export const getTypographyStyle = (variant: keyof typeof typography) => 
  typography[variant];

export const getMetricCardTheme = (theme: keyof typeof metricCardThemes) => 
  metricCardThemes[theme];

// CSS-in-JS utility for styled components
export const materialTheme = {
  colors: materialColors,
  spacing,
  elevation,
  borderRadius,
  typography,
  motion: {
    curves: motionCurves,
    durations: motionDurations,
  },
  dashboard: dashboardGrid,
} as const;

export type MaterialTheme = typeof materialTheme;
export type MaterialColor = keyof typeof materialColors;
export type MetricCardTheme = keyof typeof metricCardThemes;